import { AnimoLabel, LabelDataUtil } from "web/utils/LabelDataUtil";
import { log } from "web/utils/logger";

export class BLEService {
  static SERVICE_UUID = '412b2781-2987-4446-9c62-fc2e526286a4';
  private static LABEL_CHAR_UUID = 'b5f53348-c601-471d-8ede-f90b24760875';
  private static DEVICE_CONTROL_CHAR_UUID = '00005476-98ba-dcfe-1032-547698badcfe';
  private static EPOCH_TIME_CHAR_UUID = '441251cc-7bfb-4417-a344-88cd678a9ed3';
  private static MAX_ERR_MSEC = 500;
  private static BATTERY_LEVEL_CHAR_UUID = '99995476-98ba-dcfe-1032-547698badcfe';
  private static TRANSMISSION_TIMEOUT = 600000; // 10 minutes in milliseconds (600 seconds) - accommodate flow control delays
  private static LABEL_SIZE = 38;
  private static DATA_EXPORT_CHAR_UUID = '190993f1-c81d-492b-a829-804f28b9745d';
  private static FILESYSTEM_MANAGEMENT_CHAR_UUID = '44445476-98ba-dcfe-1032-547698badcfe';
  private static INDICATE_CHARACTERISTIC_UUID = '88885476-98ba-dcfe-1032-547698badcfe';
  private static FIRMWARE_VERSION_CHRC_UUID = '872e3697-c06e-4ee2-b2b4-b97f5668bd31';
  static ACTIVE_RECORDING_CHAR_UUID = '66665476-98ba-dcfe-1032-547698badcfe';
  private static LOG_DUMP_UUID = '22225476-98ba-dcfe-1032-547698badcfe';
  private static DEVICE_STATISTICS_UUID = '1b8b0a21-6dfe-4b56-9a1e-1b5a3d2c7f10';

  // Batch Mode UUIDs (Operating Mode and Batch State characteristics)
  private static OPERATING_MODE_CHAR_UUID = 'bbbb5476-98ba-dcfe-1032-547698badcfe';
  private static BATCH_STATE_CHAR_UUID = 'cccc5476-98ba-dcfe-1032-547698badcfe';

  // Credential storage UUIDs
  private static ORG_ID_UUID = '3c4e5a6b-7d8e-4f9a-0b1c-2d3e4f5a6b7c';
  private static ORG_NAME_UUID = '3d4e5a6b-7d8e-4f9a-0b1c-2d3e4f5a6b7d';
  private static WIFI_SSID_UUID = '4d5e6f7a-8b9c-4d0e-1f2a-3b4c5d6e7f8a';
  private static WIFI_PASSWORD_UUID = '5e6f7a8b-9c0d-4e1f-2a3b-4c5d6e7f8a9b';
  private static WIFI_SCAN_UUID = '3e4f5a6b-7c8d-4e9f-a0b1-c2d3e4f5a6b7';
  private static SERVER_HOST_UUID = '8a9b0c1d-2e3f-4a5b-6c7d-8e9f0a1b2c3d';
  // HTTP Cookie UUIDs (2 characteristics: data + control)
  private static HTTP_COOKIE_DATA_UUID = '6f7a8b9c-0d1e-4f2a-3b4c-5d6e7f8a9b0c';
  private static HTTP_COOKIE_CONTROL_UUID = '7a8b9c0d-1e2f-4a3b-4c5d-6e7f8a9b0c1d';
  private static HTTP_COOKIE_CHUNK_SIZE = 243;  // Data per chunk (1 byte reserved for index)
  private static HTTP_COOKIE_NUM_CHUNKS = 17;

  // Credential Write Status UUID (for acknowledgments)
  private static CREDENTIAL_STATUS_UUID = '2f3e4d5c-6b7a-4890-a1b2-c3d4e5f6a7b8';

  // Credential write type enum (matching firmware)
  private static CRED_WRITE_TYPE = {
    ORG_ID: 0x01,
    ORG_NAME: 0x02,
    SERVER_HOST: 0x03,
    WIFI_SSID: 0x04,
    WIFI_PASSWORD: 0x05,
    HTTP_COOKIE_CHUNK: 0x06,
  } as const;

  // Credential write status enum (matching firmware)
  private static CRED_STATUS = {
    SUCCESS: 0x00,
    FAILURE: 0x01,
    IN_PROGRESS: 0x02,
  } as const;

  // Enum for device operation modes (matching firmware implementation)
  private static DEVICE_MODES = {
    MODE_STANDBY: 0x00,
    MODE_STREAMING: 0x01,
    MODE_CONTINUOUS_STREAMING: 0x02,  // Renamed from MODE_PASSIVE_RECORDING - continuous audio streaming to cloud
    MODE_DATA_EXPORT: 0x03,
    MODE_ACTIVE_RECORDING: 0x04,
    MODE_SCHEDULED_RECORDING: 0x05,
    MODE_DFU: 0x08,  // DFU mode for firmware updates (pauses app, keeps BLE active)
    MODE_BATCH: 0x09,  // Batch Mode - manual recording mode with user-controlled start/stop
    MODE_NA_RECORDING: 0xFF,
  } as const;

  /**
   * Wait for credential write acknowledgment from device
   * @param characteristic The credential status characteristic
   * @param expectedWriteType The type of write we're waiting for
   * @param expectedChunkIndex For cookie chunks, the expected chunk index (otherwise 0)
   * @param timeoutMs Timeout in milliseconds (default 5000)
   * @returns Promise that resolves when SUCCESS received, rejects on FAILURE or timeout
   */
  private static async waitForCredentialAck(
    characteristic: BluetoothRemoteGATTCharacteristic,
    expectedWriteType: number,
    expectedChunkIndex: number = 0,
    timeoutMs: number = 5000
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let timeoutId: NodeJS.Timeout;
      let listener: ((event: Event) => void) | null = null;
      let resolved = false;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (listener) {
          try {
            characteristic.removeEventListener('characteristicvaluechanged', listener);
          } catch (e) {
            // Ignore cleanup errors
            console.warn('Error removing listener:', e);
          }
          listener = null;
        }
      };

      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          reject(new Error(`Timeout waiting for ACK (type=${expectedWriteType}, chunk=${expectedChunkIndex})`));
        }
      }, timeoutMs);

      listener = (event: Event) => {
        if (resolved) return; // Already resolved/rejected
        
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        const value = target.value;

        if (!value || value.byteLength < 3) return;

        const writeType = value.getUint8(0);
        const status = value.getUint8(1);
        const chunkIndex = value.getUint8(2);

        // Check if this is the ACK we're waiting for
        if (writeType === expectedWriteType && chunkIndex === expectedChunkIndex) {
          if (status === this.CRED_STATUS.SUCCESS) {
            resolved = true;
            cleanup();
            log(`ACK received: type=${writeType}, chunk=${chunkIndex} - SUCCESS`);
            resolve();
          } else if (status === this.CRED_STATUS.FAILURE) {
            resolved = true;
            cleanup();
            log(`ACK received: type=${writeType}, chunk=${chunkIndex} - FAILURE`);
            reject(new Error(`Write failed on device (type=${writeType}, chunk=${chunkIndex})`));
          } else if (status === this.CRED_STATUS.IN_PROGRESS) {
            // Log IN_PROGRESS but keep waiting for SUCCESS/FAILURE
            log(`ACK received: type=${writeType}, chunk=${chunkIndex} - IN_PROGRESS`);
          }
        }
      };

      // Add listener BEFORE setting up timeout to ensure we don't miss early notifications
      characteristic.addEventListener('characteristicvaluechanged', listener);
    });
  }

  /**
   * Helper to write a credential with ACK - reusable for all credential types
   * @param statusCharacteristic Optional pre-configured status characteristic with notifications enabled
   */
  private static async writeCredentialWithAck(
    device: BluetoothDevice,
    characteristicUUID: string,
    value: Uint8Array,
    writeType: number,
    credentialName: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    const MAX_RETRIES = 3;
    const ACK_TIMEOUT_MS = 2000; // 2 seconds per attempt (increased from 1s)
    
    let lastError: Error | null = null;
    
    // Retry loop
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const server = await device.gatt?.connect();
        if (!server) throw new Error('Failed to connect to GATT server');

        const service = await server.getPrimaryService(this.SERVICE_UUID);
        const characteristic = await service.getCharacteristic(characteristicUUID);

        // Use provided status characteristic or get a new one
        let shouldStopNotifications = false;
        if (!statusCharacteristic) {
          statusCharacteristic = await service.getCharacteristic(this.CREDENTIAL_STATUS_UUID);
          await statusCharacteristic.startNotifications();
          shouldStopNotifications = true;
          
          // Add delay after starting notifications to ensure subscription is fully ready
          await new Promise(resolve => setTimeout(resolve, 150));
        }

        // Start waiting for ACK BEFORE writing value to avoid race condition
        // The listener is set up synchronously inside waitForCredentialAck
        const ackPromise = this.waitForCredentialAck(statusCharacteristic, writeType, 0, ACK_TIMEOUT_MS);
        
        // Small delay to ensure listener is fully attached
        await new Promise(resolve => setTimeout(resolve, 50));

        // Write value
        await characteristic.writeValue(value);
        log(`${credentialName} write sent (attempt ${attempt}/${MAX_RETRIES}), waiting for ACK...`);

        // Wait for ACK (listener is already set up)
        await ackPromise;
        log(`${credentialName} written successfully with ACK on attempt ${attempt}`);

        // Only stop notifications if we started them
        if (shouldStopNotifications) {
          await statusCharacteristic.stopNotifications();
        }
        
        return; // Success - exit the function
        
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < MAX_RETRIES) {
          log(`${credentialName} write attempt ${attempt}/${MAX_RETRIES} failed: ${(error as Error).message}`);
          log(`Retrying ${credentialName} write in 300ms...`);
          // Longer delay before retry to let BLE stack fully settle
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
          // Last attempt failed
          console.error(`${credentialName} write failed after ${MAX_RETRIES} attempts:`, error);
        }
      }
    }
    
    // If we get here, all retries failed
    throw lastError || new Error(`Failed to write ${credentialName} after ${MAX_RETRIES} attempts`);
  }

  static async requestDevice(name?: string, additionalServices?: string[], namePrefix?: string): Promise<BluetoothDevice> {
    try {
      // Build the list of optional services (always include our main service)
      const optionalServices = [this.SERVICE_UUID, ...(additionalServices || [])];

      // Web Bluetooth namePrefix is case-sensitive, so include common casings
      const filters: BluetoothLEScanFilter[] = name
        ? [{ name }]
        : namePrefix
          ? [
              { namePrefix: namePrefix.toUpperCase(), services: [this.SERVICE_UUID] },
              { namePrefix: namePrefix.toLowerCase(), services: [this.SERVICE_UUID] },
            ]
          : [{ services: [this.SERVICE_UUID] }];

      return await navigator.bluetooth.requestDevice({ filters, optionalServices });
    } catch (error) {
      console.error('Error requesting BLE device:', error);
      throw error;
    }
  }

  static async readNumberOfLabels(device: BluetoothDevice): Promise<number> {
    try {
      // log('Connecting to device to read number of labels...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      // log('Getting primary service...');
      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.LABEL_CHAR_UUID);

      // log('Reading label count...');
      const value = await characteristic.readValue();
      const labelCount = new DataView(value.buffer).getInt32(0, true);
      // log(`Found ${labelCount} labels on device`);
      return labelCount;
    } catch (error) {
      console.error('Error reading labels:', error);
      throw error;
    }
  }

  static async writeDeviceMode(device: BluetoothDevice, mode: keyof typeof BLEService.DEVICE_MODES): Promise<void> {
    try {
      // log(`Setting device mode to: ${mode}`);
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.DEVICE_CONTROL_CHAR_UUID);

      const command = new Uint8Array(16);
      command[0] = 0x01;
      command[1] = this.DEVICE_MODES[mode];

      //   // log(`Writing mode command: [${Array.from(command).map(b => '0x' + b.toString(16)).join(', ')}]`);
      await characteristic.writeValue(command);
      // log('Device mode set successfully');
    } catch (error) {
      console.error('Error writing device mode:', error);
      throw error;
    }
  }

  static async setBleScanEnabled(device: BluetoothDevice, enabled: boolean): Promise<void> {
    try {
      log(`Setting BLE scan to: ${enabled ? 'enabled' : 'disabled'}`);
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.DEVICE_CONTROL_CHAR_UUID);

      const command = new Uint8Array(2);
      command[0] = 0x03; // COMMAND_SET_BLE_SCAN_ENABLED
      command[1] = enabled ? 0x01 : 0x00;

      await characteristic.writeValue(command);
      log('BLE scan setting updated successfully');
    } catch (error) {
      console.error('Error setting BLE scan:', error);
      throw error;
    }
  }

  static async readDeviceMode(device: BluetoothDevice): Promise<string> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.DEVICE_CONTROL_CHAR_UUID);

      const value = await characteristic.readValue();
      const mode = value.getUint8(0);

      // Convert mode number back to string key
      const modeKey = Object.entries(this.DEVICE_MODES).find(([_, value]) => value === mode)?.[0];
      return modeKey || 'UNKNOWN';
    } catch (error) {
      console.error('Error reading device mode:', error);
      throw error;
    }
  }

  /**
   * Enter DFU mode before firmware update
   * This puts the device in a minimal state that pauses app operations while keeping BLE active
   * @param device The connected Bluetooth device
   * @returns Promise<boolean> - true if DFU mode entered successfully, false otherwise (never throws)
   */
  static async enterDFUMode(device: BluetoothDevice): Promise<boolean> {
    try {
      log('Entering DFU mode for firmware update...');
      const server = await device.gatt?.connect();
      if (!server) {
        console.warn('Failed to connect to GATT server for DFU mode, continuing without DFU mode');
        return false;
      }

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.DEVICE_CONTROL_CHAR_UUID);

      const command = new Uint8Array(16);
      command[0] = 0x01; // COMMAND_SET_MODE
      command[1] = this.DEVICE_MODES.MODE_DFU; // 0x08

      await characteristic.writeValue(command);
      log('Device set to DFU mode successfully');

      // Wait for device to enter DFU mode and stabilize
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    } catch (error) {
      console.warn('Could not enter DFU mode, continuing with firmware update anyway:', error);
      // Log the specific error type for debugging
      const errorMsg = (error as Error).message;
      if (errorMsg.includes('characteristic') || errorMsg.includes('not found')) {
        log('DFU mode characteristic not found (older firmware version)');
      } else if (errorMsg.includes('GATT')) {
        log('GATT operation failed - device may not support DFU mode');
      } else {
        log(`DFU mode entry failed: ${errorMsg}`);
      }
      // Never throw - always allow firmware update to continue
      return false;
    }
  }

  /**
   * Exit DFU mode after firmware update
   * Returns device to standby mode
   * @param device The connected Bluetooth device
   * @throws Error if the operation fails
   */
  static async exitDFUMode(device: BluetoothDevice): Promise<void> {
    try {
      log('Exiting DFU mode, returning to standby...');
      await this.writeDeviceMode(device, 'MODE_STANDBY');
      log('Device returned to standby mode');
    } catch (error) {
      console.error('Error exiting DFU mode:', error);
      // Non-critical: device will return to normal mode on next boot anyway
      log('Failed to exit DFU mode, but device will recover on next boot');
    }
  }

  static async readEpochTimestamp(device: BluetoothDevice): Promise<number> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.EPOCH_TIME_CHAR_UUID);

      const value = await characteristic.readValue();

      // Convert 8 bytes to timestamp (little-endian)
      let epochTimeMsec = 0;
      for (let i = 0; i < 8; i++) {
        epochTimeMsec += value.getUint8(i) * Math.pow(256, i);
      }
      console.log('Epoch time in milliseconds:', epochTimeMsec);

      return epochTimeMsec;
    } catch (error) {
      console.error('Error reading epoch timestamp:', error);
      throw error;
    }
  }

  static async writeEpochTime(device: BluetoothDevice, msec: number): Promise<void> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.EPOCH_TIME_CHAR_UUID);

      // Convert the epoch time to an 8-byte buffer (64-bit integer)
      const buffer = new Uint8Array(8);
      const view = new DataView(buffer.buffer);

      // Write the timestamp in little-endian format
      view.setUint32(0, msec & 0xFFFFFFFF, true);  // Lower 32 bits (little-endian)
      view.setUint32(4, Math.floor(msec / 0x100000000), true);  // Upper 32 bits (little-endian)

      console.log('Writing epoch time (milliseconds):', msec);
      await characteristic.writeValue(buffer);
    } catch (error) {
      console.error('Error writing epoch time:', error);
      throw error;
    }
  }

  static async syncEpochTime(device: BluetoothDevice): Promise<void> {
    try {
      const timestampBefore = Date.now();
      const deviceTimestamp = await this.readEpochTimestamp(device);
      const timestampAfter = Date.now();

      log('Device Timestamp:', deviceTimestamp);
      log('Current Timestamp Before Sync (UTC):', timestampBefore);
      log('Current Timestamp After Sync (UTC):', timestampAfter);

      if (deviceTimestamp < (timestampBefore - this.MAX_ERR_MSEC) ||
        deviceTimestamp > (timestampAfter + this.MAX_ERR_MSEC)) {
        // Use UTC time directly without timezone adjustment
        log('Syncing device time to UTC time:', timestampAfter);
        await this.writeEpochTime(device, timestampAfter);
      } else {
        log('Device time is up-to-date, no sync needed.');
      }
    } catch (error) {
      console.error('Error syncing epoch time:', error);
      throw error;
    }
  }

  static async readBatteryLevel(device: BluetoothDevice): Promise<number> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.BATTERY_LEVEL_CHAR_UUID);

      const value = await characteristic.readValue();

      // Convert to float (4 bytes, little-endian)
      const dataView = new DataView(value.buffer);
      const batteryLevel = dataView.getFloat32(0, true); // true for little-endian

      return batteryLevel;
    } catch (error) {
      console.error('Error reading battery level:', error);
      throw error;
    }
  }

  static checkLowBattery(batteryLevel: number): boolean {
    return batteryLevel >= 3.0 && batteryLevel <= 3.4;
  }

  static async downloadLabels(device: BluetoothDevice, numberOfLabels: number): Promise<AnimoLabel[]> {
    try {
      // log(`Starting download of ${numberOfLabels} labels...`);
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.LABEL_CHAR_UUID);

      // log('Enabling notifications for label downloads...');
      await characteristic.startNotifications();

      const labels: AnimoLabel[] = [];
      let timeoutId: NodeJS.Timeout;

      return new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          console.warn('Label download timed out after', this.TRANSMISSION_TIMEOUT, 'ms');
          characteristic.stopNotifications().catch(console.error);
          reject(new Error('Label download timeout'));
        }, this.TRANSMISSION_TIMEOUT);

        characteristic.addEventListener('characteristicvaluechanged', (event: Event) => {
          const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
          if (!value) return;

          const buffer = value.buffer;
          if (buffer.byteLength % this.LABEL_SIZE !== 0) {
            console.warn(`Invalid label data size: ${buffer.byteLength}`);
            return;
          }

          //   const labelsInPacket = buffer.byteLength / this.LABEL_SIZE;
          // log(`Received packet with ${labelsInPacket} labels`);

          // Process each label in the buffer
          for (let i = 0; i < buffer.byteLength; i += this.LABEL_SIZE) {
            const labelBuffer = buffer.slice(i, i + this.LABEL_SIZE);
            const label = LabelDataUtil.handleLabelData(labelBuffer as ArrayBuffer);
            labels.push(label);

            // log(`Processed label ${labels.length}/${numberOfLabels}`);
            if (labels.length === numberOfLabels) {
              // log('All labels received successfully');
              clearTimeout(timeoutId);
              characteristic.stopNotifications().catch(console.error);
              resolve(labels);
            }
          }
        });

        // log('Writing start command for label download...');
        const startCommand = new Uint8Array([0x01]);
        characteristic.writeValue(startCommand).catch(error => {
          console.error('Failed to write start command:', error);
          clearTimeout(timeoutId);
          characteristic.stopNotifications().catch(console.error);
          reject(error);
        });
      });

    } catch (error) {
      console.error('Error downloading labels:', error);
      throw error;
    }
  }

  static async transferTimeSeriesFiles(
    device: BluetoothDevice,
    uploadCallback: (file: File) => Promise<void>,
    onProgress?: (fileId: number, progress: number) => void  // Add progress callback
  ): Promise<void> {
    // log('Starting time series file transfer...');
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');
      // log('Connected to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      // log('Got primary service');

      const characteristic = await service.getCharacteristic(this.DATA_EXPORT_CHAR_UUID);
      // log('Got data export characteristic');

      // Enable notifications
      await characteristic.startNotifications();
      // log('Enabled notifications for data export');

      return new Promise((resolve, reject) => {
        let fileBuffer: number[] = [];
        let fileId = 0;
        let expectedBytes = 0;
        let receivedHeaderPage = false;
        let timeoutId: NodeJS.Timeout;
        let totalBytesReceived = 0;
        let fileCount = 0;
        let lastActivityTimestamp = Date.now();
        let retryCount = 0;
        const MAX_RETRIES = 3;

        // Flow control state
        let bodyChunksReceived = 0;
        const BATCH_SIZE = 20;

        const TRANSMISSION_TIMEOUT = 600000; // 10 minutes (600 seconds) - accommodate flow control delays
        const HEADER_MSG_HEADER_SIZE = 6;
        const DATA_MSG_HEADER_SIZE = 4;
        const ACTIVITY_TIMEOUT = 15000; // 15 seconds of inactivity before trying to recover

        const resetTimeout = () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            console.warn('Transfer timed out after', TRANSMISSION_TIMEOUT, 'ms');
            characteristic.stopNotifications().catch(console.error);
            reject(new Error('Time series transfer timeout'));
          }, TRANSMISSION_TIMEOUT);
        };

        // Function to handle situations where we get a message for an unexpected file ID
        const handleUnexpectedFileId = (expectedId: number, receivedId: number, msgType: number) => {
          console.warn(`Received file ID mismatch: expected ${expectedId}, got ${receivedId}, type: ${msgType}`);
          
          // If we got a new header, start over with that file ID
          if (msgType === 0) { // Header
            console.log(`Adapting to new file ID ${receivedId}`);
            return true; // Allow this to be processed as a new file
          }
          
          // For body/end messages with wrong ID, ignore
          return false;
        };

        // Setup activity watchdog timer
        const activityWatchdog = setInterval(() => {
          const inactiveTime = Date.now() - lastActivityTimestamp;
          if (inactiveTime > ACTIVITY_TIMEOUT && receivedHeaderPage) {
            console.warn(`No activity for ${inactiveTime}ms, attempting recovery`);
            
            // Only attempt recovery if we're in the middle of receiving a file
            if (receivedHeaderPage && retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(`Recovery attempt ${retryCount}/${MAX_RETRIES}`);
              
              // Send acknowledgment to try to proceed
              const ackBuffer = new Uint8Array(2);
              ackBuffer[0] = fileId & 0xFF;
              ackBuffer[1] = (fileId >> 8) & 0xFF;
              characteristic.writeValue(ackBuffer).catch(error => {
                console.error('Failed to write recovery ack:', error);
              });
              
              // Reset last activity timestamp to prevent immediate retry
              lastActivityTimestamp = Date.now();
            } else if (retryCount >= MAX_RETRIES) {
              console.error(`Max retries (${MAX_RETRIES}) reached, giving up on current file`);
              
              // Reset for next file
              fileBuffer = [];
              receivedHeaderPage = false;
              totalBytesReceived = 0;
              
              // Request next file
              const nextFileIdBuffer = new Uint8Array(2);
              nextFileIdBuffer[0] = 0;  // First byte of file ID
              nextFileIdBuffer[1] = 0;  // Second byte of file ID
              characteristic.writeValue(nextFileIdBuffer).catch(console.error);
              
              retryCount = 0;
              lastActivityTimestamp = Date.now();
            }
          }
        }, 5000);

        characteristic.addEventListener('characteristicvaluechanged', async (event: Event) => {
          const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
          if (!value) {
            console.warn('Received empty value notification');
            return;
          }

          resetTimeout();
          lastActivityTimestamp = Date.now();

          const dataView = new DataView(value.buffer);
          const msgTypeAndFileId = dataView.getUint16(0, true);
          const msgType = msgTypeAndFileId & 3;
          const currentFileId = msgTypeAndFileId >> 2;

          // log(`Received packet - Type: ${msgType}, FileID: ${currentFileId}, Size: ${value.byteLength} bytes`);

          switch (msgType) {
            case 0: // Header
              if (receivedHeaderPage && currentFileId === fileId) {
                console.warn('Received duplicate header for the same file ID');
                // Don't reject - just ignore duplicate headers
                return;
              }
              
              // If we get a header for a new file ID, that's allowed
              if (currentFileId !== fileId) {
                console.log(`Switching to new file ID: ${currentFileId}`);
                // Reset state for the new file
                fileBuffer = [];
                totalBytesReceived = 0;
                retryCount = 0;
                bodyChunksReceived = 0; // Reset chunk counter for new file
              }

              expectedBytes = dataView.getUint32(2, true);
              fileId = currentFileId;
              receivedHeaderPage = true;
              fileCount++;

              console.log(`Starting new file ${fileId} - Expected size: ${expectedBytes} bytes`);

              // Copy header data excluding header metadata
              for (let i = HEADER_MSG_HEADER_SIZE; i < value.byteLength; i++) {
                fileBuffer.push(value.getUint8(i));
              }
              totalBytesReceived = value.byteLength - HEADER_MSG_HEADER_SIZE;

              // Send HEADER_ACK (fileId | 0x8000)
              const headerAck = new Uint8Array(2);
              new DataView(headerAck.buffer).setUint16(0, fileId | 0x8000, true);
              await characteristic.writeValue(headerAck);
              console.log(`HEADER_ACK sent for file ${fileId}`);

              resetTimeout(); // Reset timeout after ACK sent
              // log(`Header processed - Buffer size: ${fileBuffer.length}`);
              break;

            case 1: // Body
              if (!receivedHeaderPage) {
                console.warn('Skipping body packet - Header not received');
                return;
              }
              
              if (currentFileId !== fileId) {
                if (!handleUnexpectedFileId(fileId, currentFileId, msgType)) {
                  console.warn(`Skipping body packet - Mismatched file ID: expected ${fileId}, got ${currentFileId}`);
                  return;
                }
              }

              // Copy body data excluding message header
              for (let i = DATA_MSG_HEADER_SIZE; i < value.byteLength; i++) {
                fileBuffer.push(value.getUint8(i));
              }
              totalBytesReceived += value.byteLength - DATA_MSG_HEADER_SIZE;
              bodyChunksReceived++;

              const progress = ((totalBytesReceived / expectedBytes) * 100).toFixed(1);
              // log(`File ${fileId} progress: ${progress}% (${totalBytesReceived}/${expectedBytes} bytes)`);
              onProgress?.(fileId, parseFloat(progress));

              // Send BATCH_ACK every 20 BODY chunks
              if (bodyChunksReceived % BATCH_SIZE === 0) {
                const batchAck = new Uint8Array(2);
                // Send sequence number with BATCH_ACK flag (0x4000)
                new DataView(batchAck.buffer).setUint16(0, bodyChunksReceived | 0x4000, true);
                await characteristic.writeValue(batchAck);
                console.log(`BATCH_ACK sent for chunk ${bodyChunksReceived} (batch complete)`);

                resetTimeout(); // Reset timeout after ACK sent
              }
              break;

            case 2: // End
              if (currentFileId === 0 && !receivedHeaderPage) {
                console.log('No more files available');
                clearTimeout(timeoutId);
                clearInterval(activityWatchdog);
                characteristic.stopNotifications().catch(console.error);
                resolve();
                return;
              }

              if (currentFileId !== fileId) {
                console.warn(`Mismatched file ID in end notification: expected ${fileId}, got ${currentFileId}`);
                
                if (currentFileId === 0) {
                  // Special case: device is telling us it's done
                  console.log('Device signaling no more files with ID 0 end message');
                  clearTimeout(timeoutId);
                  clearInterval(activityWatchdog);
                  characteristic.stopNotifications().catch(console.error);
                  resolve();
                  return;
                }
                
                // For other mismatches, ignore this end message
                return;
              }

              // Handle empty or incomplete file case
              if (fileBuffer.length !== expectedBytes) {
                console.warn(`File size mismatch: expected ${expectedBytes}, got ${fileBuffer.length}`);
                
                // If we have enough data (over 90%), try to process it anyway
                if (fileBuffer.length > 0 && (fileBuffer.length / expectedBytes) > 0.9) {
                  console.log(`Processing partial file (${fileBuffer.length}/${expectedBytes} bytes)`);
                } else if (fileBuffer.length === 0) {
                  console.warn('Empty file received, skipping upload');
                  
                  // Acknowledge receipt even for empty files to keep the protocol moving
                  const ackBuffer = new Uint8Array(2);
                  ackBuffer[0] = fileId & 0xFF;
                  ackBuffer[1] = (fileId >> 8) & 0xFF;
                  console.log(`Sending acknowledgment for empty file ${fileId}`);
                  await characteristic.writeValue(ackBuffer);
                  
                  // Reset for next file
                  fileBuffer = [];
                  receivedHeaderPage = false;
                  totalBytesReceived = 0;
                  retryCount = 0;
                  return;
                } else {
                  // If we have very little data, skip this file
                  console.warn(`File too incomplete (${fileBuffer.length}/${expectedBytes} bytes), skipping`);
                  
                  // Acknowledge receipt to keep the protocol moving
                  const ackBuffer = new Uint8Array(2);
                  ackBuffer[0] = fileId & 0xFF;
                  ackBuffer[1] = (fileId >> 8) & 0xFF;
                  console.log(`Sending acknowledgment for incomplete file ${fileId}`);
                  await characteristic.writeValue(ackBuffer);
                  
                  // Reset for next file
                  fileBuffer = [];
                  receivedHeaderPage = false;
                  totalBytesReceived = 0;
                  retryCount = 0;
                  return;
                }
              }

              try {
                const file = new File([new Uint8Array(fileBuffer)], `timeseries_${Date.now()}.bin`, {
                  type: 'application/octet-stream'
                });
                console.log(`Uploading file ${fileId} (${file.size} bytes) to server...`);

                // CRITICAL: Wait for HTTP upload to complete BEFORE sending ACK
                // This ensures file is safely stored on server before device deletes it
                await uploadCallback(file);
                console.log(`Successfully uploaded file ${fileId} to server`);

                // Only send ACK after successful HTTP upload
                const ackBuffer = new Uint8Array(2);
                ackBuffer[0] = fileId & 0xFF;
                ackBuffer[1] = (fileId >> 8) & 0xFF;
                console.log(`Sending acknowledgment for file ${fileId}, firmware will delete file and send next`);
                await characteristic.writeValue(ackBuffer);

                resetTimeout(); // Reset timeout after file ACK sent

                // Reset for next file
                fileBuffer = [];
                receivedHeaderPage = false;
                totalBytesReceived = 0;
                retryCount = 0;
                bodyChunksReceived = 0; // Reset chunk counter for next file

              } catch (error) {
                console.error(`Error uploading file ${fileId} to server:`, error);

                // DON'T send ACK if upload failed - this preserves the file on device
                console.error('NOT sending ACK - file remains on device for retry');
                clearInterval(activityWatchdog);
                characteristic.stopNotifications().catch(console.error);
                reject(error);
              }
              break;

            default:
              console.warn(`Unknown message type received: ${msgType}`);
          }
        });

        // Write initial command with flow control flags enabled
        // Flags: HEADER_ACK (0x8000) | BATCH_ACK (0x4000) | INDICATION_MODE (0x2000) | batch_size (20)
        // 0x8000 | 0x4000 | 0x2000 | 20 = 0xE014
        const HEADER_ACK_FLAG = 0x8000;
        const BATCH_ACK_FLAG = 0x4000;
        const INDICATION_MODE_FLAG = 0x2000;
        const DEFAULT_BATCH_SIZE = 20;

        const flags = HEADER_ACK_FLAG | BATCH_ACK_FLAG | INDICATION_MODE_FLAG | DEFAULT_BATCH_SIZE;
        const startBuffer = new Uint8Array(2);
        new DataView(startBuffer.buffer).setUint16(0, flags, true); // Little-endian: 0x14 0xE0

        console.log(`Sending initial command with flow control enabled: 0x${flags.toString(16)}`);
        characteristic.writeValue(startBuffer).catch(error => {
          console.error('Failed to write start command:', error);
          clearTimeout(timeoutId);
          clearInterval(activityWatchdog);
          characteristic.stopNotifications().catch(console.error);
          reject(error);
        });

        resetTimeout();
      });
    } catch (error) {
      console.error('Error in transferTimeSeriesFiles:', error);
      throw error;
    }
  }

  /**
   * Clears all labels from the device
   * @param device The connected Bluetooth device
   * @throws Error if the operation fails
   */
  static async writeClearAllLabels(device: BluetoothDevice): Promise<void> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.LABEL_CHAR_UUID);

      // Create a buffer with value 2 (matching Android implementation)
      const clearBuffer = new Uint8Array([2]);
      await characteristic.writeValue(clearBuffer);

      // Wait for the clear operation to complete (matching Android's delay)
      await new Promise(resolve => setTimeout(resolve, 3000));

      // log('Labels cleared successfully');
    } catch (error) {
      console.error('Error clearing labels:', error);
      throw error;
    }
  }

  static async clearAllRecording(device: BluetoothDevice, doEraseAllFlash: boolean): Promise<void> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.FILESYSTEM_MANAGEMENT_CHAR_UUID);

      const value = new Uint8Array(1);
      value[0] = doEraseAllFlash ? 0x01 : 0x02;

      await characteristic.writeValue(value);
      log('All recordings cleared successfully');
    } catch (error) {
      console.error('Error clearing all recordings:', error);
      throw error;
    }
  }

  static async locateDevice(device: BluetoothDevice): Promise<void> {
    try {
      log('Attempting to connect to GATT server...');
      const server = await device.gatt?.connect();
      if (!server) {
        console.error('Failed to connect to GATT server');
        throw new Error('Failed to connect to GATT server');
      }
      log('Connected to GATT server successfully.');

      log(`Attempting to get primary service: ${this.SERVICE_UUID}`);
      const service = await server.getPrimaryService(this.SERVICE_UUID);
      if (!service) {
        console.error(`Primary service ${this.SERVICE_UUID} not found`);
        throw new Error(`Primary service ${this.SERVICE_UUID} not found`);
      }
      log(`Primary service ${this.SERVICE_UUID} found successfully.`);

      log(`Attempting to get characteristic: ${this.INDICATE_CHARACTERISTIC_UUID}`);
      const characteristic = await service.getCharacteristic(this.INDICATE_CHARACTERISTIC_UUID);
      if (!characteristic) {
        console.error(`Indicate characteristic ${this.INDICATE_CHARACTERISTIC_UUID} not found`);
        throw new Error(`Indicate characteristic ${this.INDICATE_CHARACTERISTIC_UUID} not found`);
      }
      log(`Indicate characteristic ${this.INDICATE_CHARACTERISTIC_UUID} found successfully.`);

      // Write the indicate command to the characteristic
      const indicateCommand = new Uint8Array([0x01]); // Command to indicate
      log('Writing indicate command to the characteristic...');
      await characteristic.writeValue(indicateCommand);
      log('Indicate command sent successfully.');
    } catch (error) {
      console.error('Error locating device:', error);
      throw error;
    }
  }

  static async readFirmwareVersion(device: BluetoothDevice): Promise<string | undefined> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const firmwareCharacteristic = await service.getCharacteristic(this.FIRMWARE_VERSION_CHRC_UUID);
      const value = await firmwareCharacteristic.readValue();

      const firmwareVersion = new TextDecoder('ascii').decode(value.buffer as ArrayBuffer);
      return firmwareVersion;
    } catch (error) {
      console.error('Error reading firmware version:', error);
      throw error;
    }
  }

  /**
   * Start monitoring active recording notifications from the device
   * @param device The connected Bluetooth device
   * @param callback Callback to receive notifications when active recording status changes
   * @returns A function to stop notifications
   */
  static async startMonitoringActiveRecording(
    device: BluetoothDevice, 
    callback: (isRecording: boolean) => void
  ): Promise<() => Promise<void>> {
    try {
      log('Starting active recording monitoring...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      console.log(`Attempting to get ACTIVE_RECORDING_CHAR_UUID: ${this.ACTIVE_RECORDING_CHAR_UUID}`);
      const characteristic = await service.getCharacteristic(this.ACTIVE_RECORDING_CHAR_UUID);
      console.log('Successfully obtained ACTIVE_RECORDING_CHAR_UUID characteristic');

      // Enable notifications
      await characteristic.startNotifications();
      
      // Add event listener for characteristic value changes
      const listener = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        const value = target.value;
        
        if (!value) return;
        
        // Check if the first byte is 0x02, which means active recording
        const firstByte = value.getUint8(0);
        console.log(`Active recording notification value: 0x${firstByte.toString(16)}`);
        const isRecording = firstByte === 0x02;
        console.log(`Is recording: ${isRecording} (0x02 = ${firstByte === 0x02})`);
        log(`Active recording notification: ${isRecording ? 'Recording' : 'Not recording'}`);
        
        // Call callback with the new status
        callback(isRecording);
      };
      
      characteristic.addEventListener('characteristicvaluechanged', listener);
      
      // Return function to stop monitoring
      return async () => {
        try {
          await characteristic.stopNotifications();
          characteristic.removeEventListener('characteristicvaluechanged', listener);
          log('Active recording monitoring stopped');
        } catch (error) {
          console.error('Error stopping active recording monitoring:', error);
        }
      };
    } catch (error) {
      console.error('Error starting active recording monitoring:', error);
      throw error;
    }
  }

  /**
   * Read device statistics from the device
   * @param device The connected Bluetooth device
   * @returns Promise<DeviceStatistics> - Object containing file statistics
   */
  static async readDeviceStatistics(device: BluetoothDevice): Promise<DeviceStatistics> {
    try {
      log('Reading device statistics...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.DEVICE_STATISTICS_UUID);

      const value = await characteristic.readValue();

      // Decode as UTF-8 and parse JSON
      const jsonString = new TextDecoder('utf-8').decode(value.buffer);
      const statistics: DeviceStatistics = JSON.parse(jsonString);

      log('Device statistics retrieved:', statistics);
      return statistics;
    } catch (error) {
      console.error('Error reading device statistics:', error);
      throw error;
    }
  }

  /**
   * Retrieve raw logs from the device
   * @param device The connected Bluetooth device
   * @param onProgress Optional callback for progress updates
   * @returns Promise<Uint8Array> - Raw log data
   */
  static async retrieveRawLogs(
    device: BluetoothDevice,
    onProgress?: (totalBytes: number) => void
  ): Promise<Uint8Array> {
    try {
      log('Starting raw log retrieval...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const logDumpCharacteristic = await service.getCharacteristic(this.LOG_DUMP_UUID);
      const controlCharacteristic = await service.getCharacteristic(this.DEVICE_CONTROL_CHAR_UUID);

      // Read current device state
      const stateValue = await controlCharacteristic.readValue();
      const originalState = stateValue.getUint8(0);
      log('Original device state:', originalState);

      // Switch to standby mode
      const standbyCommand = new Uint8Array(2);
      standbyCommand[0] = 0x01; // Command type
      standbyCommand[1] = this.DEVICE_MODES.MODE_STANDBY; // Standby mode
      await controlCharacteristic.writeValue(standbyCommand);
      log('Switched to standby mode');

      // Wait a bit for mode switch
      await new Promise(resolve => setTimeout(resolve, 2500));

      const logData: Uint8Array[] = [];
      let timeoutActive = false;

      return new Promise((resolve, reject) => {
        let timeoutId: NodeJS.Timeout;

        const notificationHandler = (event: Event) => {
          const target = event.target as BluetoothRemoteGATTCharacteristic;
          const value = target.value;

          if (!value) return;

          const data = new Uint8Array(value.buffer);
          logData.push(data);
          timeoutActive = true;

          const totalBytes = logData.reduce((sum, chunk) => sum + chunk.length, 0);
          log(`Received ${data.length} bytes, total: ${totalBytes} bytes`);

          if (onProgress) {
            onProgress(totalBytes);
          }
        };

        const cleanup = async () => {
          try {
            await logDumpCharacteristic.stopNotifications();
            logDumpCharacteristic.removeEventListener('characteristicvaluechanged', notificationHandler);

            // Restore original device state
            const restoreCommand = new Uint8Array(2);
            restoreCommand[0] = 0x01;
            restoreCommand[1] = originalState;
            await controlCharacteristic.writeValue(restoreCommand);
            log('Restored original device state');
          } catch (error) {
            console.error('Error during cleanup:', error);
          }
        };

        const checkTimeout = async () => {
          await new Promise(resolve => setTimeout(resolve, 5000));
          if (!timeoutActive) {
            log('No more data received, finishing log retrieval');
            clearTimeout(timeoutId);
            await cleanup();

            // Combine all log data
            const totalLength = logData.reduce((sum, chunk) => sum + chunk.length, 0);
            const combinedData = new Uint8Array(totalLength);
            let offset = 0;

            for (const chunk of logData) {
              combinedData.set(chunk, offset);
              offset += chunk.length;
            }

            log(`Log retrieval completed, total: ${totalLength} bytes`);
            resolve(combinedData);
          } else {
            timeoutActive = false;
            checkTimeout();
          }
        };

        // Setup timeout
        timeoutId = setTimeout(async () => {
          log('Log retrieval timed out');
          await cleanup();
          reject(new Error('Log retrieval timeout'));
        }, this.TRANSMISSION_TIMEOUT);

        // Start notifications and request logs
        logDumpCharacteristic.startNotifications()
          .then(() => {
            logDumpCharacteristic.addEventListener('characteristicvaluechanged', notificationHandler);

            // Write request to stream log file
            const logRequest = new Uint8Array(1);
            logRequest[0] = 0; // Request log dump
            return logDumpCharacteristic.writeValue(logRequest);
          })
          .then(() => {
            log('Log dump request sent');
            checkTimeout();
          })
          .catch(async (error) => {
            console.error('Error starting log retrieval:', error);
            clearTimeout(timeoutId);
            await cleanup();
            reject(error);
          });
      });
    } catch (error) {
      console.error('Error retrieving raw logs:', error);
      throw error;
    }
  }

  /**
   * Read organization ID from device
   */
  static async readOrgId(device: BluetoothDevice): Promise<string> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.ORG_ID_UUID);

      const value = await characteristic.readValue();
      if (value.byteLength === 0) {
        return '';
      }
      return new TextDecoder('utf-8').decode(value.buffer);
    } catch (error) {
      console.error('Error reading organization ID:', error);
      throw error;
    }
  }

  /**
   * Write organization ID to device (max 36 characters) with ACK
   * @param statusCharacteristic Optional pre-configured status characteristic
   */
  static async writeOrgId(
    device: BluetoothDevice,
    orgId: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    if (orgId.length > 36) {
      throw new Error(`Organization ID too long (${orgId.length} chars, max 36)`);
    }

    return this.writeCredentialWithAck(
      device,
      this.ORG_ID_UUID,
      new TextEncoder().encode(orgId),
      this.CRED_WRITE_TYPE.ORG_ID,
      'Organization ID',
      statusCharacteristic
    );
  }

  /**
   * Read organization name from device
   */
  static async readOrgName(device: BluetoothDevice): Promise<string> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.ORG_NAME_UUID);

      const value = await characteristic.readValue();
      if (value.byteLength === 0) {
        return '';
      }
      return new TextDecoder('utf-8').decode(value.buffer);
    } catch (error) {
      console.error('Error reading organization name:', error);
      throw error;
    }
  }

  /**
   * Write organization name to device (max 64 characters) with ACK
   * @param statusCharacteristic Optional pre-configured status characteristic
   */
  static async writeOrgName(
    device: BluetoothDevice,
    orgName: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    if (orgName.length > 64) {
      throw new Error(`Organization name too long (${orgName.length} chars, max 64)`);
    }

    return this.writeCredentialWithAck(
      device,
      this.ORG_NAME_UUID,
      new TextEncoder().encode(orgName),
      this.CRED_WRITE_TYPE.ORG_NAME,
      'Organization Name',
      statusCharacteristic
    );
  }

  /**
   * Read WiFi SSID from device
   */
  static async readWifiSsid(device: BluetoothDevice): Promise<string> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.WIFI_SSID_UUID);

      const value = await characteristic.readValue();
      if (value.byteLength === 0) {
        return '';
      }
      return new TextDecoder('utf-8').decode(value.buffer);
    } catch (error) {
      console.error('Error reading WiFi SSID:', error);
      throw error;
    }
  }

  /**
   * Write WiFi SSID to device (max 32 characters) with ACK
   * @param statusCharacteristic Optional pre-configured status characteristic
   */
  static async writeWifiSsid(
    device: BluetoothDevice,
    ssid: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    if (ssid.length > 32) {
      throw new Error(`WiFi SSID too long (${ssid.length} chars, max 32)`);
    }

    return this.writeCredentialWithAck(
      device,
      this.WIFI_SSID_UUID,
      new TextEncoder().encode(ssid),
      this.CRED_WRITE_TYPE.WIFI_SSID,
      'WiFi SSID',
      statusCharacteristic
    );
  }

  /**
   * Write WiFi password to device (max 64 characters, write-only) with ACK
   * @param statusCharacteristic Optional pre-configured status characteristic
   */
  static async writeWifiPassword(
    device: BluetoothDevice,
    password: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    if (password.length > 64) {
      throw new Error(`WiFi password too long (${password.length} chars, max 64)`);
    }

    return this.writeCredentialWithAck(
      device,
      this.WIFI_PASSWORD_UUID,
      new TextEncoder().encode(password),
      this.CRED_WRITE_TYPE.WIFI_PASSWORD,
      'WiFi Password',
      statusCharacteristic
    );
  }

  static async clearWifiCredentials(device: BluetoothDevice): Promise<void> {
    log('Clearing WiFi credentials...');
    await this.writeWifiSsid(device, '');
    await this.writeWifiPassword(device, '');
    log('WiFi credentials cleared');
  }

  /**
   * Read server host from device
   */
  static async readServerHost(device: BluetoothDevice): Promise<string> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.SERVER_HOST_UUID);

      const value = await characteristic.readValue();
      if (value.byteLength === 0) {
        return '';
      }
      return new TextDecoder('utf-8').decode(value.buffer);
    } catch (error) {
      console.error('Error reading server host:', error);
      throw error;
    }
  }

  /**
   * Write server host to device (max 255 characters) with ACK
   * @param statusCharacteristic Optional pre-configured status characteristic
   */
  static async writeServerHost(
    device: BluetoothDevice,
    serverHost: string,
    statusCharacteristic?: BluetoothRemoteGATTCharacteristic
  ): Promise<void> {
    if (serverHost.length > 255) {
      throw new Error(`Server host too long (${serverHost.length} chars, max 255)`);
    }

    return this.writeCredentialWithAck(
      device,
      this.SERVER_HOST_UUID,
      new TextEncoder().encode(serverHost),
      this.CRED_WRITE_TYPE.SERVER_HOST,
      'Server Host',
      statusCharacteristic
    );
  }

  /**
   * Scan for available WiFi networks
   * Returns an array of WifiNetwork objects with SSID and optional RSSI
   * Supports both old format (string[]) and new format ({ssid, rssi}[])
   */
  static async scanWifiNetworks(device: BluetoothDevice): Promise<WifiNetwork[]> {
    try {
      log('Scanning for WiFi networks...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.WIFI_SCAN_UUID);

      const value = await characteristic.readValue();
      if (value.byteLength === 0) {
        log('No WiFi networks found');
        return [];
      }

      const jsonString = new TextDecoder('utf-8').decode(value.buffer);
      const parsed: unknown[] = JSON.parse(jsonString);

      if (parsed.length === 0) {
        return [];
      }

      let networks: WifiNetwork[];

      if (typeof parsed[0] === 'string') {
        // Old format: ["SSID1", "SSID2"]
        networks = (parsed as string[])
          .filter(ssid => ssid && ssid.trim().length > 0)
          .map(ssid => ({ ssid, rssi: null }));
      } else {
        // New format: [{"ssid": "SSID1", "rssi": -45}, ...]
        networks = (parsed as Array<{ ssid: string; rssi?: number }>)
          .filter(n => n.ssid && n.ssid.trim().length > 0)
          .map(n => ({ ssid: n.ssid, rssi: n.rssi ?? null }));
      }

      // Sort by signal strength: strongest first (closest to 0)
      // Networks without RSSI go to the end
      networks.sort((a, b) => {
        if (a.rssi === null && b.rssi === null) return 0;
        if (a.rssi === null) return 1;
        if (b.rssi === null) return -1;
        return b.rssi - a.rssi;
      });

      log(`Found ${networks.length} WiFi networks`);
      return networks;
    } catch (error) {
      console.error('Error scanning WiFi networks:', error);
      throw error;
    }
  }

  /**
   * Write HTTP cookie to device using new 2-UUID protocol with ACK (write-only for security)
   * Protocol: Write to HTTP_COOKIE_DATA_UUID with format [chunk_index:1 byte][data:up to 243 bytes]
   * Each chunk write is acknowledged by the device before proceeding to the next chunk
   */
  static async writeHttpCookie(device: BluetoothDevice, cookie: string): Promise<void> {
    const maxSize = this.HTTP_COOKIE_CHUNK_SIZE * this.HTTP_COOKIE_NUM_CHUNKS;
    if (cookie.length > maxSize) {
      throw new Error(`HTTP cookie too long (${cookie.length} bytes, max ${maxSize})`);
    }

    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const cookieBytes = new TextEncoder().encode(cookie);

      log(`Writing HTTP cookie (${cookieBytes.length} bytes)...`);

      // Get characteristics
      const dataCharacteristic = await service.getCharacteristic(this.HTTP_COOKIE_DATA_UUID);
      const statusCharacteristic = await service.getCharacteristic(this.CREDENTIAL_STATUS_UUID);

      // Enable ACK notifications
      await statusCharacteristic.startNotifications();

      // Add small delay after starting notifications to ensure subscription is fully established
      await new Promise(resolve => setTimeout(resolve, 100));

      // Write chunks using new protocol: [chunk_index][data]
      // Wait for ACK after each chunk before proceeding
      const MAX_RETRIES = 3;
      const ACK_TIMEOUT_MS = 2000; // 2 seconds per chunk attempt (increased from 1s)
      
      for (let i = 0; i < this.HTTP_COOKIE_NUM_CHUNKS; i++) {
        const start = i * this.HTTP_COOKIE_CHUNK_SIZE;
        const end = Math.min(start + this.HTTP_COOKIE_CHUNK_SIZE, cookieBytes.length);

        if (start >= cookieBytes.length) {
          break;
        }

        const chunkData = cookieBytes.slice(start, end);

        // Prepend chunk index to data
        const chunkWithIndex = new Uint8Array(1 + chunkData.length);
        chunkWithIndex[0] = i;  // Chunk index
        chunkWithIndex.set(chunkData, 1);  // Chunk data

        // Retry logic for each chunk
        let lastError: Error | null = null;
        let chunkSuccess = false;
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            // Start waiting for ACK BEFORE writing chunk
            const ackPromise = this.waitForCredentialAck(statusCharacteristic, this.CRED_WRITE_TYPE.HTTP_COOKIE_CHUNK, i, ACK_TIMEOUT_MS);
            
            // Small delay to ensure listener is fully attached
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Write chunk
            await dataCharacteristic.writeValue(chunkWithIndex);
            log(`Chunk ${i}: ${chunkData.length} bytes written (attempt ${attempt}/${MAX_RETRIES}), waiting for ACK...`);

            // Wait for ACK for this specific chunk
            await ackPromise;
            log(`Chunk ${i} acknowledged successfully on attempt ${attempt}`);
            
            chunkSuccess = true;
            break; // Success - move to next chunk
            
          } catch (error) {
            lastError = error as Error;
            
            if (attempt < MAX_RETRIES) {
              log(`Chunk ${i} attempt ${attempt}/${MAX_RETRIES} failed: ${(error as Error).message}`);
              log(`Retrying chunk ${i} in 300ms...`);
              await new Promise(resolve => setTimeout(resolve, 300));
            } else {
              console.error(`Chunk ${i} failed after ${MAX_RETRIES} attempts:`, error);
            }
          }
        }
        
        if (!chunkSuccess) {
          throw lastError || new Error(`Failed to write chunk ${i} after ${MAX_RETRIES} attempts`);
        }
      }

      // Stop notifications
      await statusCharacteristic.stopNotifications();

      log('HTTP cookie written successfully with all ACKs received');
    } catch (error) {
      console.error('Error writing HTTP cookie:', error);
      throw error;
    }
  }

  /**
   * Clear HTTP cookie using control characteristic
   * Protocol: Write 0x00 to HTTP_COOKIE_CONTROL_UUID to clear
   */
  static async clearHttpCookie(device: BluetoothDevice): Promise<void> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const controlCharacteristic = await service.getCharacteristic(this.HTTP_COOKIE_CONTROL_UUID);

      // Write clear command (0x00)
      await controlCharacteristic.writeValue(new Uint8Array([0x00]));
      log('HTTP cookie cleared successfully');
    } catch (error) {
      console.error('Error clearing HTTP cookie:', error);
      throw error;
    }
  }

  /**
   * Get the credential status characteristic for shared notification session
   * @internal Helper for managing notification sessions across multiple writes
   */
  static async getCredentialStatusCharacteristic(device: BluetoothDevice): Promise<BluetoothRemoteGATTCharacteristic> {
    const server = await device.gatt?.connect();
    if (!server) throw new Error('Failed to connect to GATT server');

    const service = await server.getPrimaryService(this.SERVICE_UUID);
    return await service.getCharacteristic(this.CREDENTIAL_STATUS_UUID);
  }

  /**
   * Write all WiFi credentials at once
   */
  static async writeWifiCredentials(device: BluetoothDevice, ssid: string, password: string): Promise<void> {
    await this.writeWifiSsid(device, ssid);
    await this.writeWifiPassword(device, password);
  }

  /**
   * Send a manual reset command to restart the device
   * Useful after updating credentials to ensure they are loaded
   * @param device The connected Bluetooth device
   * @throws Error if the operation fails
   */
  static async resetDevice(device: BluetoothDevice): Promise<void> {
    try {
      log('Sending device reset command...');
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);

      // This is the manual reset characteristic
      const characteristic = await service.getCharacteristic(this.FILESYSTEM_MANAGEMENT_CHAR_UUID);

      // Write reset command
      // Byte 0: 0x00 = normal reset (not flash erase)
      // Byte 1: 0x00 = no coredump trigger
      const resetCommand = new Uint8Array(16);
      resetCommand[0] = 0x00;  // Normal reset (not flash erase)
      resetCommand[1] = 0x00;  // Don't trigger coredump

      await characteristic.writeValue(resetCommand);
      log('Device reset command sent successfully');

      // Note: Device will disconnect immediately after reset
      // Wait a moment before the device actually resets
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error resetting device:', error);
      throw error;
    }
  }

  /**
   * Read the current operating mode from the device
   * @param device The connected Bluetooth device
   * @returns Promise<number> - The operating mode value (0x02 = Continuous Streaming, 0x09 = Batch Mode)
   * @throws Error if the operation fails
   */
  static async readOperatingMode(device: BluetoothDevice): Promise<number> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.OPERATING_MODE_CHAR_UUID);

      const value = await characteristic.readValue();
      const mode = value.getUint8(0);

      log(`Operating mode read: 0x${mode.toString(16).padStart(2, '0')}`);
      return mode;
    } catch (error) {
      console.error('Error reading operating mode:', error);
      throw error;
    }
  }

  /**
   * Write the operating mode to the device
   * @param device The connected Bluetooth device
   * @param mode The operating mode to set (0x02 = Continuous Streaming, 0x09 = Batch Mode)
   * @throws Error if the operation fails
   */
  static async writeOperatingMode(device: BluetoothDevice, mode: 0x02 | 0x09): Promise<void> {
    try {
      log(`Writing operating mode: 0x${mode.toString(16).padStart(2, '0')}`);
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.OPERATING_MODE_CHAR_UUID);

      const modeValue = new Uint8Array([mode]);
      await characteristic.writeValue(modeValue);

      log('Operating mode written successfully');

      // Wait for mode switch to process (up to 250ms per guide, we wait 500ms to be safe)
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error writing operating mode:', error);
      throw error;
    }
  }

  /**
   * Read the batch recording state from the device
   * Only meaningful when device is in Batch Mode (0x09)
   * @param device The connected Bluetooth device
   * @returns Promise<number> - The batch state (0x00 = NOT_RECORDING, 0x01 = RECORDING)
   * @throws Error if the operation fails
   */
  static async readBatchState(device: BluetoothDevice): Promise<number> {
    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to GATT server');

      const service = await server.getPrimaryService(this.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(this.BATCH_STATE_CHAR_UUID);

      const value = await characteristic.readValue();
      const state = value.getUint8(0);

      log(`Batch state read: 0x${state.toString(16).padStart(2, '0')} (${state === 0x01 ? 'RECORDING' : 'NOT_RECORDING'})`);
      return state;
    } catch (error) {
      console.error('Error reading batch state:', error);
      throw error;
    }
  }
}

export interface DeviceStatistics {
  f_created: number;
  f_exported: number;
  f_deleted: number;
  f_flushed: number;
  f_errors: number;
  f_corrupt: number;
  bleScan?: number; // 0 = disabled, 1 = enabled
}

export interface DeviceCredentials {
  orgId: string;
  orgName: string;
  wifiSsid: string;
  wifiPassword: string;
  httpCookie: string;
}

export interface WifiNetwork {
  ssid: string;
  rssi: number | null; // null = old firmware, no RSSI available
}
